import { z } from 'zod';
import { CodFieldValuesSchema } from './cod-form.schema';

// Line item schema for cart and 1-page checkout
export const CheckoutLineItemSchema = z.object({
  variantId: z.string().uuid(),
  productId: z.string().uuid(),
  quantity: z.number().int().positive().default(1),
  unitPrice: z.number().nonnegative().optional(),
  customAttributes: z.record(z.string(), z.any()).optional()
});

// Customer delivery address and contact details
export const CustomerCheckoutSchema = z.object({
  fullName: z.string().min(2, 'Full name is required (at least 2 characters)'),
  phone: z.string().min(6, 'Valid phone number is required'),
  email: z.string().email().optional().or(z.literal('')),
  countryCode: z.string().length(2).default('MA'),
  city: z.string().min(1, 'City is required'),
  address: z.string().min(3, 'Delivery address is required'),
  note: z.string().max(500).optional()
});

// Complete checkout payload schema including line items, shipping, and ad pixels
export const CreateCheckoutDtoSchema = z.object({
  checkoutId: z.string().uuid().optional(),
  funnelId: z.string().uuid().optional(),
  stepId: z.string().uuid().optional(),
  shippingRateId: z.string().uuid().optional(),
  lineItems: z.array(CheckoutLineItemSchema)
    .min(1, 'At least one item is required')
    .max(50, 'An order can have at most 50 line items'),
  // The order bump the shopper ticked, as an offer id only: the server re-resolves and re-prices it,
  // so a browser can never name the price of the line it is asking for.
  acceptedUpsellOfferId: z.string().uuid().optional(),
  customer: CustomerCheckoutSchema,
  paymentMethod: z.enum(['COD', 'STRIPE', 'CMI', 'PAYZONE']).default('COD'),
  discountCode: z.string().optional(),
  idempotencyKey: z.string().max(128).optional(),
  utmParameters: z.object({
    utmSource: z.string().optional(),
    utmMedium: z.string().optional(),
    utmCampaign: z.string().optional(),
    utmContent: z.string().optional(),
    utmTerm: z.string().optional()
  }).optional(),
  // What the conversion APIs match a shopper on: the pixels' cookies, their browser, and the page they ordered from.
  pixelTracking: z.object({
    fbp: z.string().max(500).optional(),
    fbc: z.string().max(500).optional(),
    ttp: z.string().max(500).optional(),
    clientIp: z.string().max(64).optional(),
    userAgent: z.string().max(1000).optional(),
    sourceUrl: z.string().url().max(2048).optional(),
    externalId: z.string().uuid().optional()
  }).optional()
});

export const CreateCheckoutSchema = CreateCheckoutDtoSchema;

// The customer block as it arrives on the wire: every part optional, because the store's COD
// configuration — not this schema — decides which of them a shopper actually has to fill in.
export const CustomerCheckoutInputSchema = z.object({
  fullName: z.string().max(255).optional(),
  phone: z.string().max(50).optional(),
  email: z.string().max(255).optional(),
  countryCode: z.string().length(2).optional(),
  city: z.string().max(100).optional(),
  address: z.string().max(500).optional(),
  note: z.string().max(500).optional()
});

// What the checkout endpoints accept. `codFields` carries answers keyed by their immutable
// storage key; the legacy `customer` block is still read and folded into the same map.
export const CodCheckoutInputSchema = CreateCheckoutDtoSchema.omit({ customer: true }).extend({
  customer: CustomerCheckoutInputSchema.optional(),
  codFields: CodFieldValuesSchema.optional(),
  // A note the shopper wrote in the cart, kept on the order whether or not the store's form asks for notes.
  orderNote: z.string().trim().max(500).optional()
});

export type CustomerCheckoutInputDto = z.infer<typeof CustomerCheckoutInputSchema>;
export type CodCheckoutInputDto = z.infer<typeof CodCheckoutInputSchema>;

// Query schema for the abandoned-cart recovery list
export const AbandonedCheckoutQuerySchema = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50)
});

// The windows a store may choose between, in minutes, before an unfinished checkout counts as abandoned.
export const ABANDONED_CHECKOUT_DELAYS = [10, 60, 360, 1440] as const;

export type AbandonedCheckoutDelay = (typeof ABANDONED_CHECKOUT_DELAYS)[number];

// What a store that never chose a window uses: the shortest one, so a cart surfaces while it is still warm.
export const DEFAULT_ABANDONED_CHECKOUT_DELAY: AbandonedCheckoutDelay = 10;

// Where the chosen window lives inside the tenant settings blob; one value per store.
export const ABANDONED_CHECKOUT_DELAY_KEY = 'abandonedCheckoutDelayMinutes';

// Reports whether a number is one of the offered windows, so the UI and the API can never drift apart.
export const isAbandonedCheckoutDelay = (value: number): value is AbandonedCheckoutDelay =>
  (ABANDONED_CHECKOUT_DELAYS as readonly number[]).includes(value);

// The stored window as it arrives on a settings patch; anything outside the offered list is refused.
export const AbandonedCheckoutDelaySchema = z
  .number()
  .int()
  .refine(isAbandonedCheckoutDelay, `Choose one of ${ABANDONED_CHECKOUT_DELAYS.join(', ')} minutes`);

// Reads a store's abandonment window out of its settings blob, falling back to the platform default.
export function resolveAbandonedCheckoutDelay(
  settings: Record<string, unknown> | null | undefined
): AbandonedCheckoutDelay {
  const stored = settings?.[ABANDONED_CHECKOUT_DELAY_KEY];
  if (typeof stored === 'number' && isAbandonedCheckoutDelay(stored)) return stored;
  return DEFAULT_ABANDONED_CHECKOUT_DELAY;
}

export type AbandonedCheckoutQuery = z.infer<typeof AbandonedCheckoutQuerySchema>;
export type CheckoutLineItemDto = z.infer<typeof CheckoutLineItemSchema>;
export type CustomerCheckoutDto = z.infer<typeof CustomerCheckoutSchema>;
export type CreateCheckoutDto = z.infer<typeof CreateCheckoutDtoSchema>;
